import { PAGE_NAMES, PAGE_GROUPS, MENU_SUBGROUPS } from '@/constants/pages';
import { permissions, PermissionsDefinition } from '@/store/modules/PositionModule';

export interface Page {
  icon: string;
  group: string | null;
  name: string;
  permission: PermissionsDefinition[];
  // Pages sharing a subGroup are rendered as one collapsible menu item.
  subGroup?: MENU_SUBGROUPS;
}

interface UsePage {
  pages: Record<PAGE_NAMES, Page>;
  getTranslateByName: (n: string) => string;
}

const pages = {
  [PAGE_NAMES.INDEX]: {
    name: PAGE_NAMES.INDEX,
    icon: '',
    group: null,
    permission: [],
  },
  [PAGE_NAMES.LOGIN]: {
    name: PAGE_NAMES.LOGIN,
    icon: '',
    group: null,
    permission: [],
  },
  [PAGE_NAMES.PUBLIC]: {
    name: PAGE_NAMES.PUBLIC,
    icon: '',
    group: null,
    permission: [],
  },
  [PAGE_NAMES.SUBMIT_FEEDBACK]: {
    name: PAGE_NAMES.SUBMIT_FEEDBACK,
    icon: '',
    group: null,
    permission: [],
  },
  [PAGE_NAMES.APPROVE_ABSENCE]: {
    name: PAGE_NAMES.APPROVE_ABSENCE,
    icon: '',
    group: null,
    permission: [],
  },
  [PAGE_NAMES.APP]: {
    name: PAGE_NAMES.INDEX,
    icon: '',
    group: null,
    permission: [],
  },
  [PAGE_NAMES.DASHBOARD]: {
    name: PAGE_NAMES.DASHBOARD,
    icon: 'mdi-apps',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [permissions.HUB_DASHBOARD.READ_ALL],
  },
  [PAGE_NAMES.SITDOWN]: {
    name: PAGE_NAMES.SITDOWN,
    icon: 'mdi-checkbox-blank-circle-outline',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [permissions.HUB_SITDOWN.READ_ALL],
  },
  [PAGE_NAMES.ABSENCES]: {
    name: PAGE_NAMES.ABSENCES,
    icon: 'mdi-palm-tree',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [permissions.HUB_OFFICE_ABSENCES.READ_OWN],
  },
  [PAGE_NAMES.ABSENCES_APPROVING]: {
    name: PAGE_NAMES.ABSENCES_APPROVING,
    icon: 'mdi-account-check',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [permissions.HUB_OFFICE_ABSENCES_REQUESTS.READ_OWN],
  },
  [PAGE_NAMES.GAME]: {
    name: PAGE_NAMES.GAME,
    icon: 'mdi-chart-box-outline',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [permissions.HUB_GAME.MANAGE_ALL, permissions.HUB_GAME.READ_ALL],
  },
  [PAGE_NAMES.ACHIEVEMENTS]: {
    name: PAGE_NAMES.ACHIEVEMENTS,
    icon: 'mdi-medal-outline',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [permissions.HUB_ACHIEVEMENTS.READ_ALL],
  },
  [PAGE_NAMES.USER_GOALS]: {
    name: PAGE_NAMES.USER_GOALS,
    icon: 'mdi-flag-checkered',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [
      permissions.HUB_USERS_GOALS.READ_OWN,
      permissions.HUB_USERS_GOALS.MANAGE_ALL,
      permissions.HUB_USERS_GOALS.MANAGE_TEAM_MEMBER,
    ],
  },
  [PAGE_NAMES.USER_1ON1S]: {
    name: PAGE_NAMES.USER_1ON1S,
    icon: 'mdi-heart',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [
      permissions.HUB_USERS_1ON1.READ_OWN,
      permissions.HUB_USERS_1ON1.MANAGE_ALL,
      permissions.HUB_USERS_1ON1.MANAGE_TEAM_MEMBER,
    ],
  },
  [PAGE_NAMES.WORKLOGS]: {
    name: PAGE_NAMES.WORKLOGS,
    icon: 'mdi-clock-time-eight-outline',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [permissions.HUB_WORKLOGS.READ_OWN, permissions.HUB_WORKLOGS.READ_ALL],
  },
  [PAGE_NAMES.WORKING_TIME_RECORDS]: {
    name: PAGE_NAMES.WORKING_TIME_RECORDS,
    icon: 'mdi-account-cog',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [
      permissions.HUB_WORK_TIME_RECORDS.READ_ALL,
      permissions.HUB_WORK_TIME_RECORDS.READ_OWN,
      permissions.HUB_WORK_TIME_RECORDS.MANAGE_ALL,
      permissions.HUB_WORK_TIME_RECORDS.UPDATE_OWN,
      permissions.HUB_WORK_TIME_RECORDS.UPDATE_STATUS,
    ],
  },
  [PAGE_NAMES.COLLEAGUES]: {
    name: PAGE_NAMES.COLLEAGUES,
    icon: 'mdi-account-multiple',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [permissions.HUB_COLLEAGUES.READ_ALL],
  },
  [PAGE_NAMES.COLLEAGUES_DETAIL]: {
    name: PAGE_NAMES.COLLEAGUES_DETAIL,
    icon: '',
    group: null,
    permission: [permissions.HUB_COLLEAGUES.READ_ALL],
  },
  [PAGE_NAMES.FEEDBACK_HEATMAP]: {
    name: PAGE_NAMES.FEEDBACK_HEATMAP,
    icon: 'mdi-emoticon-outline',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [permissions.HUB_FEEDBACKS.READ_TEAM_MEMBER, permissions.HUB_FEEDBACKS.READ_ALL],
  },
  [PAGE_NAMES.COLLABORATOR_NOTES]: {
    name: PAGE_NAMES.COLLABORATOR_NOTES,
    icon: 'mdi-text-box-edit',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [
      permissions.HUB_COLLABORATOR_NOTES.MANAGE_ALL,
      permissions.HUB_COLLABORATOR_NOTES.MANAGE_TEAM_MEMBER,
      permissions.HUB_COLLABORATOR_NOTES.READ_ALL,
      permissions.HUB_COLLABORATOR_NOTES.READ_TEAM_MEMBER,
    ],
  },
  [PAGE_NAMES.COLLABORATOR_HISTORY]: {
    name: PAGE_NAMES.COLLABORATOR_HISTORY,
    icon: 'mdi-timer-sand-complete',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [
      permissions.HUB_COLLABORATOR_NOTES.MANAGE_ALL,
      permissions.HUB_COLLABORATOR_NOTES.MANAGE_TEAM_MEMBER,
      permissions.HUB_COLLABORATOR_NOTES.READ_ALL,
      permissions.HUB_COLLABORATOR_NOTES.READ_TEAM_MEMBER,
      permissions.HUB_USERS_1ON1.READ_OWN,
      permissions.HUB_USERS_1ON1.MANAGE_ALL,
      permissions.HUB_USERS_1ON1.MANAGE_TEAM_MEMBER,
      permissions.HUB_USERS_GOALS.READ_OWN,
      permissions.HUB_USERS_GOALS.MANAGE_ALL,
      permissions.HUB_USERS_GOALS.MANAGE_TEAM_MEMBER,
      permissions.HUB_FEEDBACKS.READ_OWN,
      permissions.HUB_FEEDBACKS.READ_TEAM_MEMBER,
      permissions.HUB_FEEDBACKS.READ_ALL,
    ],
  },
  [PAGE_NAMES.SECURITY_TF]: {
    name: PAGE_NAMES.SECURITY_TF,
    icon: 'mdi-security',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [],
  },
  [PAGE_NAMES.DOCUMENTATION]: {
    name: PAGE_NAMES.DOCUMENTATION,
    icon: 'mdi-bookshelf',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [],
  },
  [PAGE_NAMES.PROJECTS]: {
    name: PAGE_NAMES.PROJECTS,
    icon: 'mdi-code-braces',
    group: PAGE_GROUPS.GROUP_SETTINGS,
    permission: [permissions.HUB_PROJECTS.MANAGE_ALL],
  },
  [PAGE_NAMES.USERS]: {
    name: PAGE_NAMES.USERS,
    icon: 'mdi-account-multiple',
    group: PAGE_GROUPS.GROUP_SETTINGS,
    permission: [permissions.HUB_USERS.MANAGE_ALL],
  },
  [PAGE_NAMES.POSITIONS]: {
    name: PAGE_NAMES.POSITIONS,
    icon: 'mdi-account-star-outline',
    group: PAGE_GROUPS.GROUP_SETTINGS,
    permission: [permissions.HUB_POSITIONS.MANAGE_ALL],
  },
  [PAGE_NAMES.SUBSTITUTE_GROUPS]: {
    name: PAGE_NAMES.SUBSTITUTE_GROUPS,
    icon: 'mdi-account-group',
    group: PAGE_GROUPS.GROUP_SETTINGS,
    permission: [permissions.HUB_SUBSTITUTE_GROUPS.MANAGE_ALL],
  },
  [PAGE_NAMES.SITDOWN_TIMES]: {
    name: PAGE_NAMES.SITDOWN_TIMES,
    icon: 'mdi-alarm-plus',
    group: PAGE_GROUPS.GROUP_SETTINGS,
    permission: [permissions.HUB_SITDOWN_TIMES.CREATE],
  },
  [PAGE_NAMES.SETTINGS]: {
    name: PAGE_NAMES.SETTINGS,
    icon: 'mdi-cogs',
    group: PAGE_GROUPS.GROUP_SETTINGS,
    permission: [permissions.HUB_CONFIGURATION.READ_ALL, permissions.HUB_CONFIGURATION.MANAGE_ALL],
  },
  [PAGE_NAMES.USER_SETTINGS]: {
    name: PAGE_NAMES.USER_SETTINGS,
    icon: 'mdi-account-cog',
    group: null,
    permission: [],
  },
  [PAGE_NAMES.DOCUMENTS]: {
    name: PAGE_NAMES.DOCUMENTS,
    icon: 'mdi-file-document',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [
      permissions.HUB_DOCUMENTS.READ_ALL,
      permissions.HUB_DOCUMENTS.MANAGE_ALL,
      permissions.HUB_DOCUMENTS.READ_OWN,
    ],
  },
  [PAGE_NAMES.DOCUMENTS_CREATE]: {
    name: PAGE_NAMES.DOCUMENTS_CREATE,
    icon: '',
    group: null,
    permission: [permissions.HUB_DOCUMENTS.MANAGE_ALL],
  },
  [PAGE_NAMES.DEV_METRICS]: {
    name: PAGE_NAMES.DEV_METRICS,
    icon: 'mdi-chart-line',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [],
    subGroup: MENU_SUBGROUPS.REPORTS,
  },
  [PAGE_NAMES.PCLDX_METRICS]: {
    name: PAGE_NAMES.PCLDX_METRICS,
    icon: 'mdi-chart-line',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [],
    subGroup: MENU_SUBGROUPS.REPORTS,
  },
  [PAGE_NAMES.METABASE_METRICS]: {
    name: PAGE_NAMES.METABASE_METRICS,
    icon: 'mdi-chart-bar',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [],
    subGroup: MENU_SUBGROUPS.REPORTS,
  },
  [PAGE_NAMES.PM_METRICS]: {
    name: PAGE_NAMES.PM_METRICS,
    icon: 'mdi-chart-bar',
    group: PAGE_GROUPS.GROUP_GENERAL,
    permission: [],
    subGroup: MENU_SUBGROUPS.REPORTS,
  },
  [PAGE_NAMES.WORKING_TIME_RECORDS_ADMINISTRATION]: {
    name: PAGE_NAMES.WORKING_TIME_RECORDS_ADMINISTRATION,
    icon: 'mdi-calendar-clock',
    group: PAGE_GROUPS.GROUP_SETTINGS,
    permission: [permissions.HUB_WORK_TIME_RECORDS.MANAGE_ALL],
  },
};

const defaultTranslate = 'app';

const getTranslateByName = (name: string): string => {
  return pages[name] ? pages[name].name : defaultTranslate;
};

export const usePage: () => UsePage = () => {
  return { getTranslateByName, pages };
};
